import React, { useEffect, useRef, useState } from 'react';
import { savePath } from '../api/gameBuilderAPI';
import { ItemPicker, ItemStates, ItemWhat, Labelled, whatItIs } from './ItemFields';

/**
 * The creator reviews a mission before anything is built (backend mission_path.py): the objects in the room and how each
 * must END, in plain words ("Condenser hose: connected", "Ether bottle in Flammables cabinet"). Change a line, delete it,
 * add one, add an object. Every choice comes from what that object can do (the server's `menu`). Each edit is saved; the
 * server repairs what the game couldn't play and says so - it never blocks. The game's mechanics (what blocks what, how
 * it ends, the moves) are worked out by code. "Build this simulation" writes the words (AI call 2), builds it and the
 * Studio paints the scene. The plan's rules this design doesn't practise (the server's ⚠ notes, written for the AI's repair
 * call) are shown in the creator's words; the other adjustments only while editing.
 */
const SAVE_DELAY_MS = 400;

const has = (v) => v !== undefined && v !== null && v !== '';
const single = (line) => (Array.isArray(line.object) ? line.object.map((o) => ({ ...line, object: o })) : [line]);
// (a "says…" line is one before anything is typed in it - `has: ''`)
// an object with more than two states says the one it `becomes` (its tool kept while it's the same object)
const BECOMES = 'becomes:';
const valueOf = (line) => (line.becomes ? `${BECOMES}${line.becomes}` : line.status ? `status:${line.status}`
  : line.relation ? `rel:${line.relation}:${line.target}` : line.has !== undefined && line.has !== null ? 'has' : '');
const lineFor = (object, value, old = {}) => {
  if (value.startsWith(BECOMES)) {
    return { object, becomes: value.slice(BECOMES.length), ...(old.object === object && old.becomes && old.using ? { using: old.using } : {}) };
  }
  const [kind, a, b] = value.split(':');
  if (kind === 'status') return { object, status: a };
  if (kind === 'rel') return { object, relation: a, target: b };
  return { object, has: has(old.has) ? old.has : '', ...(old.unit ? { unit: old.unit } : {}) };
};
/** A line with only the fields the server's schema takes. */
const clean = (line) => Object.fromEntries(Object.entries(line).filter(([k, v]) => has(v) && (k !== 'last' || v)));
// a line still being written - "says…" with nothing typed yet: kept on screen, not saved until it says something
const blank = (line) => !Array.isArray(line) && (valueOf(line) === '' || (valueOf(line) === 'has' && !has(line.has)));
const slug = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'object';
const isPlace = (line) => line.relation && line.relation !== 'behind';
// one object per line, so each row reads "object: how it ends"
const flat = (path) => ({ ...path, end: path.end.flatMap((x) => (Array.isArray(x) ? [x] : single(x))) });
// what a new object IS (backend traits.py): picked up, pushed, or fixed; what goes in / on it - ItemFields.jsx
const NEW = 'new';
const noTraits = { carry: undefined, holds: undefined, inside: undefined, noun: undefined, steps: undefined };

// A ⚠ note (written for the AI's repair call: "R4 (Cover every container.) isn't in the game: ...") in the creator's words
const RULE_NOTE = /^⚠\s*R\d+ \((.+?)\) (isn't in the game|is about ORDER)/;
const ruleNote = (n) => {
  const m = RULE_NOTE.exec(n);
  if (!m) return { key: n, text: n.replace(/^⚠\s*/, ''), missing: false };
  return m[2] === 'is about ORDER'
    ? { key: n, text: `"${m[1]}" happens in a set order - under Edit the design, put its steps in that order.`, missing: false }
    : { key: n, text: m[1], missing: true };
};

export default function PathEditor({ planId, mission, view, onChange, onApprove, busy }) {
  const [path, setPath] = useState(() => flat(view.path));
  const [menu, setMenu] = useState(view.menu || { objects: {}, types: [] });
  const [notes, setNotes] = useState(view.notes || []);
  // the notes in the creator's words (backend mission_path.for_creator): [{kind: missing | look | adjusted, text}]
  const [said, setSaid] = useState(view.said || null);
  // what the creator sees first: the job in plain words (the server's `plain`); the editor only when they ask for it
  const [plain, setPlain] = useState(view.plain || null);
  const [editing, setEditing] = useState(!view.plain);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);   // edited since it was approved: approving again rebuilds the mission
  const [error, setError] = useState('');
  const [picked, setPicked] = useState([]);       // end lines ticked to become an order (by their place in the list)
  // the objects shown in full (by their place in the list): each is a card, folded to a line until opened - one just
  // added, or just made "new", opens by itself
  const [openRows, setOpenRows] = useState(() => new Set());
  const toggleRow = (n) => setOpenRows((rows) => { const next = new Set(rows); if (next.has(n)) next.delete(n); else next.add(n); return next; });
  const openRow = (n) => setOpenRows((rows) => new Set(rows).add(n));
  const timer = useRef(null);
  const latest = useRef(path);                     // what's on screen - the server's copy doesn't replace a line being written
  const pending = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  // Saves go one at a time, each from the save count the last one answered: a save from an older copy (another tab,
  // someone else) is refused - then nothing more is saved here until the page is reloaded.
  const rev = useRef(view.rev);
  const queue = useRef(Promise.resolve(true));
  const stale = useRef(false);
  const [refused, setRefused] = useState(false);
  const [rewrite, setRewrite] = useState(view.rewrite || []);     // the objects Apply writes words for again
  const flush = () => {
    clearTimeout(timer.current);
    queue.current = queue.current.then(saveNow);
    return queue.current;
  };
  const saveNow = async () => {
    const next = pending.current;
    if (!next) return true;
    if (stale.current) return false;
    pending.current = null;
    setSaving(true);
    try {
      const saved = await savePath(planId, mission.id, next, rev.current);
      rev.current = saved.rev;
      setRewrite(saved.rewrite || []);
      // the server's version: what the game can't play is gone (said in a note) - unless the creator has typed on since
      if (!pending.current && saved.path && !latest.current.end.some(blank)) setPath(flat(saved.path));
      setMenu(saved.menu); setNotes(saved.notes || []); setSaid(saved.said || null); setPlain(saved.plain || null); setError('');
      onChange?.(saved);
      return true;
    } catch (e) {
      if (/changed somewhere else/.test(e.message)) { stale.current = true; setRefused(true); }
      setError(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };
  const update = (next) => {
    setPath(next);
    latest.current = next;
    setChanged(true);
    pending.current = { ...next, start: next.start.map(clean),
      end: next.end.filter((x) => !blank(x)).map((x) => (Array.isArray(x) ? x.map(clean) : clean(x))) };
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_DELAY_MS);
  };

  const m = (id) => menu.objects[id];
  const optionsFor = (id, list, current) => {
    const opts = (m(id) || {})[list] || [];
    return current && !opts.some((o) => o.value === current) ? [...opts, { value: current, label: current.split(':').pop() }] : opts;
  };

  // ---- the room: each object, how it starts (a state, a place), what it says
  const startLine = (id, kind) => path.start.find((x) => x.object === id && (kind === 'status' ? x.status : kind === 'place' ? isPlace(x) : x.event === 'talks'));
  const setStart = (id, kind, line) => {
    const others = path.start.filter((x) => !(x.object === id && (kind === 'status' ? x.status : kind === 'place' ? isPlace(x) : x.event === 'talks')));
    update({ ...path, start: line ? [...others, line] : others });
  };
  const setObject = (n, patch) => update({ ...path, objects: path.objects.map((o, k) => (k === n ? { ...o, ...patch } : o)) });
  const addObject = () => {
    const taken = new Set(path.objects.map((o) => o.id));
    let id = 'new_object';
    for (let n = 2; taken.has(id); n += 1) id = `new_object_${n}`;
    update({ ...path, objects: [...path.objects, { id, type: 'thing', name: 'New object', carry: 'carry' }] });
    openRow(path.objects.length);
  };
  // a ready-made item, or "new" - then what it is: carried / pushed / fixed, what it holds, its two states
  const setKind = (n, value) => {
    if (value !== NEW) { setObject(n, { ...noTraits, type: value, states: undefined, do: undefined }); return; }
    setObject(n, { type: 'thing', carry: 'carry', holds: undefined, states: undefined, inside: undefined, steps: undefined });
    openRow(n);                                          // now it's described here
  };
  // a ready-made item made "new" with what it is now (carried? holds? its two state words, the one it must end in last) -
  // its own buttons and fields are left behind
  const customise = (n) => {
    const o = path.objects[n];
    const is = m(o.id)?.is || {};
    const ends = path.end.flat().find((x) => x.object === o.id && x.status);
    const words = is.words ? (ends?.status === 'off' ? [is.words[0], is.words[1]] : [is.words[1], is.words[0]]) : undefined;
    setObject(n, { type: 'thing', carry: is.carry || 'carry', holds: is.holds || undefined, states: words, inside: undefined, do: undefined });
    openRow(n);
  };
  // what an object can be, the first how it starts - what it becomes, and how, is the end lines' (an older design's steps
  // on the object are read as those by the server)
  const setStates = (n, states) => setObject(n, { states: states.some((w) => w.trim()) ? states : undefined, steps: undefined });
  const renameObject = (n, name) => {
    const o = path.objects[n];
    // a new object takes its id from its name, so lines can name it
    if (o.id.startsWith('new_object') && !path.end.flat().some((x) => x.object === o.id || x.target === o.id)) {
      const taken = new Set(path.objects.filter((_, j) => j !== n).map((x) => x.id));
      let id = slug(name);
      for (let k = 2; taken.has(id); k += 1) id = `${slug(name)}_${k}`;
      setObject(n, { name, id });
    } else setObject(n, { name });
  };
  const removeObject = (n) => {
    const gone = path.objects[n].id;
    const uses = (x) => x.object === gone || (Array.isArray(x.object) && x.object.includes(gone)) || x.target === gone;
    setOpenRows((rows) => new Set([...rows].filter((k) => k !== n).map((k) => (k > n ? k - 1 : k))));
    // ... and a line done with it is done without it now (a button, or the other tools)
    const unused = (x) => (x.using?.includes(gone) ? { ...x, using: x.using.length > 1 ? x.using.filter((t) => t !== gone) : undefined } : x);
    update({ ...path, objects: path.objects.filter((_, k) => k !== n), start: path.start.filter((x) => !uses(x)),
      end: path.end.map((x) => (Array.isArray(x) ? x.filter((y) => !uses(y)).map(unused) : x)).filter((x) => (Array.isArray(x) ? x.length : !uses(x)))
        .map((x) => (Array.isArray(x) ? x : unused(x))) });
  };

  // An object folded to a line: what it is, the states it can be, the words on it or what they say
  const summary = (o, mo, says) => {
    const kind = mo.kind === 'person' ? 'Person' : o.carry ? 'New' : menu.types.find((t) => t.type === o.type)?.name || o.type;
    const is = o.carry ? whatItIs({ carry: o.carry, holds: o.holds }) : mo.kind === 'person' ? '' : whatItIs({ ...mo.is, words: null });
    const states = (o.states || []).filter((w) => w.trim());
    const changes = states.length > 1 ? states.join(' / ') : mo.is?.words?.join(' / ') || '';
    const words = says?.text ? `says “${says.text}”` : o.label ? `“${o.label}” on it` : '';
    return [kind, is, changes, words].filter(Boolean).join(' · ');
  };

  // ---- how it must end
  // An order (the document says one thing must happen BEFORE another): a group of lines, done top to bottom - another
  // order is the player's mistake (backend flows.py in-order groups). Made from ticked lines; a group of one is a line.
  const makeOrder = () => {
    const ticked = [...picked].sort((a, b) => a - b);
    const group = ticked.map((k) => path.end[k]);
    const end = [];
    path.end.forEach((x, k) => {
      if (k === ticked[0]) end.push(group);
      else if (!ticked.includes(k)) end.push(x);
    });
    setPicked([]);
    update({ ...path, end });
  };
  const setGroup = (i, lines) => update({ ...path, end: lines.length > 1 ? path.end.map((x, k) => (k === i ? lines : x))
    : path.end.flatMap((x, k) => (k === i ? lines : [x])) });
  const undoOrder = (i) => update({ ...path, end: path.end.flatMap((x, k) => (k === i ? x : [x])) });
  const lineLabel = (y) => `${m(y.object)?.name || y.object}: ${optionsFor(y.object, 'end', valueOf(y)).find((o) => o.value === valueOf(y))?.label
    || (y.has !== undefined && y.has !== null ? y.has : valueOf(y).split(':').pop())}`;
  const setEnd = (i, line) => update({ ...path, end: path.end.map((x, k) => (k === i ? line : x)) });
  const removeEnd = (i) => update({ ...path, end: path.end.filter((_, k) => k !== i) });
  const canEnd = (id) => ((m(id) || {}).end || []).length > 0;
  // what the player could use on an object to get it to its state: the other things they can pick up (backend: `using`)
  const tools = (id) => path.objects.filter((o) => o.id !== id && m(o.id)?.tool);
  // A new line is one that isn't there yet (a repeat is dropped when saved): first a thing with no line, then another
  // kind of ending (a state, a place, a value) for a thing that has one - never a second state for the same thing.
  const addEnd = () => {
    const kindOf = (value) => value.split(':')[0];
    const lines = path.end.flat();
    const usedKinds = (id) => lines.filter((x) => x.object === id).map((x) => kindOf(valueOf(x)));
    const used = (id) => lines.filter((x) => x.object === id).map(valueOf);
    // a state it hasn't become yet (after how it starts) is a next step - else another kind of ending
    const ending = (id) => optionsFor(id, 'end').filter((x) => x.value.startsWith(BECOMES)).slice(1).find((x) => !used(id).includes(x.value))
      || optionsFor(id, 'end').find((x) => !x.value.startsWith(BECOMES) && !usedKinds(id).includes(kindOf(x.value)));
    const able = path.objects.filter((o) => canEnd(o.id));
    const pick = able.find((o) => !usedKinds(o.id).length) || able.find((o) => ending(o.id));
    if (!able.length) {
      setError('Nothing in this room can change yet - give an object two states, or add something that holds things.');
      return;
    }
    if (!pick) {
      setError('Every object already has each kind of ending - change a line, or add an object first.');
      return;
    }
    setError('');
    update({ ...path, end: [...path.end, lineFor(pick.id, ending(pick.id).value)] });
  };

  // One line's fields: the object, how it ends - with its value, or the tool that does it - and its delete. `set(line)`.
  const endFields = (line, set, remove, removeLabel = 'Delete this line', extra = null) => {
    const value = valueOf(line);
    const mo = m(line.object) || {};
    return (
      <>
        <select aria-label="Object" value={line.object} onChange={(e) => {
          set(lineFor(e.target.value, optionsFor(e.target.value, 'end')[0].value, line));
        }}>
          {path.objects.filter((o) => canEnd(o.id) || o.id === line.object).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          {!path.objects.some((o) => o.id === line.object) && <option value={line.object}>{line.object}</option>}
        </select>
        <select aria-label="Ends" value={value} onChange={(e) => set(lineFor(line.object, e.target.value, line))}>
          {optionsFor(line.object, 'end', value).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {line.becomes && (
          <select aria-label={`${mo.name || line.object} becomes ${line.becomes} with`} value={line.using?.[0] || ''}
            onChange={(e) => set({ ...line, using: e.target.value ? [e.target.value] : undefined })}>
            <option value="">with a button</option>
            {tools(line.object).map((t) => <option key={t.id} value={t.id}>using {t.name}</option>)}
          </select>
        )}
        {value === 'has' && (
          <>
            <input type={mo.unit ? 'number' : 'text'} step="any" aria-label="Value" value={line.has ?? ''} autoFocus={!has(line.has)}
              placeholder={mo.unit ? 'amount' : 'what it must say'} className={has(line.has) ? '' : 'is-blank'}
              onChange={(e) => set({ ...line, has: mo.unit && e.target.value !== '' ? Number(e.target.value) : e.target.value })} />
            {mo.unit && <input type="text" className="gb-path-unit" aria-label="Unit" placeholder="unit" value={line.unit || ''}
              onChange={(e) => set({ ...line, unit: e.target.value })} />}
          </>
        )}
        {extra}
        <button type="button" className="gb-link" aria-label={removeLabel} onClick={remove}>✕</button>
      </>
    );
  };
  // a two-state line done with tools from the room (any of them, several at once): chips under the line
  const toolChips = (line, set) => {
    const mo = m(line.object) || {};
    return valueOf(line).startsWith('status') && tools(line.object).length > 0 && (
      <div className="gb-path-moves" role="group" aria-label={`Tools used on ${mo.name || line.object}`}>
        <span className="gb-small gb-muted">Using (optional):</span>
        {tools(line.object).map((t) => {
          const on = (line.using || []).includes(t.id);
          const using = on ? line.using.filter((x) => x !== t.id) : [...(line.using || []), t.id];
          return (
            <button key={t.id} type="button" className={`gb-path-move ${on ? 'is-on' : ''}`} aria-pressed={on}
              onClick={() => set({ ...line, using: using.length ? using : undefined })}>{t.name}</button>
          );
        })}
      </div>
    );
  };
  const endRow = (line, i) => {
    const mo = m(line.object) || {};
    const set = (next) => setEnd(i, next);
    return (
      <li key={`end-${i}`} className="gb-path-row">
        <div className="gb-path-line">
          <input type="checkbox" aria-label={`Put ${mo.name || line.object} in an order`} title="Tick two or more to make them an order"
            checked={picked.includes(i)} onChange={() => setPicked((p) => (p.includes(i) ? p.filter((k) => k !== i) : [...p, i]))} />
          {endFields(line, set, () => removeEnd(i))}
        </div>
        {toolChips(line, set)}
      </li>
    );
  };

  // `fresh`: every object's words written again, and the scene's lines - else only new and changed objects' (the server)
  const approve = async (fresh = false) => {
    const unsaid = path.end.find(blank);
    if (unsaid) {
      setError(`Write what ${path.objects.find((o) => o.id === unsaid.object)?.name || unsaid.object} must say, or delete that line.`);
      return;
    }
    if (await flush()) onApprove(fresh);
  };

  return (
    <div className="gb-flow gb-path">
      {!editing && plain && (
        <section className="gb-panel gb-path-plain" aria-label="The job in plain words" data-tour="pe-plain">
          {plain.finds.length > 0 && (
            <>
              <h4>What the player finds</h4>
              <ul>{plain.finds.map((f) => <li key={f}>{f}</li>)}</ul>
            </>
          )}
          <h4>What the player must do</h4>
          {plain.do.length > 0
            ? <ol>{plain.do.map((d) => <li key={d}>{d}</li>)}</ol>
            : <p className="gb-small gb-muted">Nothing yet - show every detail to add what must be done.</p>}
          <p className="gb-small gb-muted">In any order. Mistakes aren&rsquo;t stopped - the player sees how it went at the end.</p>
        </section>
      )}
      {editing && (
      <>
      <p className="gb-small gb-muted">What&rsquo;s in the room and how each thing must end up once the job is done right. The player
        works it out in any order.</p>

      <section className="gb-panel" data-tour="pe-room">
        <div className="gb-obj-top">
          <h4>In the room <span className="gb-muted gb-small">({path.objects.length})</span></h4>
          {path.objects.length > 0 && (
            <button type="button" className="gb-link gb-small"
              onClick={() => setOpenRows(openRows.size ? new Set() : new Set(path.objects.map((_, k) => k)))}>
              {openRows.size ? 'Collapse all' : 'Expand all'}</button>
          )}
        </div>
        <ul className="gb-obj-list">
          {path.objects.map((o, n) => {
            const mo = m(o.id) || {};
            const status = startLine(o.id, 'status');
            const place = startLine(o.id, 'place');
            const says = startLine(o.id, 'says');
            const statusOpts = (mo.start || []).filter((x) => x.value.startsWith('status'));
            const placeOpts = (mo.start || []).filter((x) => x.value.startsWith('rel'));
            const isOpen = openRows.has(n);
            const person = mo.kind === 'person';
            const named = !o.carry && mo.named && o.states;
            const drawn = o.carry || mo.new;                   // a new object: its picture is drawn from these words
            return (
              <li key={`obj-${n}`} className={`gb-obj ${o.carry ? 'is-new' : person ? 'is-person' : 'is-ready'} ${isOpen ? 'is-open' : ''}`}>
                <div className="gb-obj-head">
                  <input type="text" className="gb-obj-name" aria-label="Object name" value={o.name} maxLength={60}
                    onChange={(e) => renameObject(n, e.target.value)} />
                  <ItemPicker label="Object type" value={o.carry ? NEW : o.type} onChange={(v) => setKind(n, v)}
                    options={[{ value: NEW, label: 'New - describe it', hint: 'say what it is below' },
                      ...(!o.carry && !menu.types.some((t) => t.type === o.type) ? [{ value: o.type, label: o.type }] : []),
                      ...menu.types.map((t) => ({ value: t.type, label: t.name, hint: t.for }))]} />
                  {!o.carry && !named && statusOpts.length > 0 && (
                    <select aria-label="Starts" value={status ? `status:${status.status}` : ''}
                      onChange={(e) => setStart(o.id, 'status', e.target.value ? lineFor(o.id, e.target.value) : null)}>
                      <option value="">starts as normal</option>
                      {statusOpts.map((x) => <option key={x.value} value={x.value}>starts {x.label}</option>)}
                    </select>
                  )}
                  {placeOpts.length > 0 && (
                    <select aria-label="Starts at" value={place ? valueOf(place) : ''}
                      onChange={(e) => setStart(o.id, 'place', e.target.value ? lineFor(o.id, e.target.value) : null)}>
                      <option value="">in the room</option>
                      {placeOpts.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                    </select>
                  )}
                  <span className="gb-obj-actions">
                    <button type="button" className="gb-obj-toggle" aria-expanded={isOpen} aria-label={`Details of ${o.name}`}
                      onClick={() => toggleRow(n)}>{isOpen ? 'Hide ▴' : 'Details ▾'}</button>
                    <button type="button" className="gb-link" aria-label={`Remove ${o.name}`} onClick={() => removeObject(n)}>✕</button>
                  </span>
                </div>
                {!isOpen && <p className="gb-obj-sum">{summary(o, mo, says)}</p>}
                {isOpen && (
                  <div className="gb-obj-body">
                    {o.carry && (
                      <section className="gb-obj-sec">
                        <h5>What it is</h5>
                        <ItemWhat name={o.name} value={o} onChange={(patch) => setObject(n, patch)} />
                      </section>
                    )}
                    {!o.carry && mo.is && !person && !mo.zone && (
                      <section className="gb-obj-sec">
                        <h5>What it is</h5>
                        <p className="gb-path-is gb-small">
                          Ready-made: {whatItIs(mo.is) || 'only moved about'}
                          <button type="button" className="gb-link" onClick={() => customise(n)}
                            title="Describe it yourself instead - its own buttons and fields are left behind">Customise</button>
                        </p>
                      </section>
                    )}
                    {(o.carry || named) && (
                      <section className="gb-obj-sec">
                        <h5>Its states</h5>
                        <ItemStates name={o.name} states={o.states?.length ? o.states : ['', '']} looks={o.state_looks}
                          more={!!o.carry} onStates={(states) => setStates(n, states)}
                          onLook={drawn ? (w, text) => setObject(n, { state_looks: { ...(o.state_looks || {}), [w]: text } }) : undefined} />
                      </section>
                    )}
                    {mo.moved && (mo.holders || []).length > 0 && (() => {
                      // moved, not carried (a forklift, a pallet): the holders it can be moved to - where it belongs and the
                      // tempting wrong ones; zones are always on offer
                      const current = o.moves_to ?? mo.moves_to ?? [];
                      return (
                        <section className="gb-obj-sec">
                          <h5>Where it can be moved</h5>
                          <div className="gb-path-moves" role="group" aria-label={`Where ${o.name} can be moved`}>
                            <span className="gb-small gb-muted">Can be moved to (and any zone):</span>
                            {mo.holders.map((h) => {
                              const on = current.includes(h.id);
                              return (
                                <button key={h.id} type="button" className={`gb-path-move ${on ? 'is-on' : ''}`} aria-pressed={on}
                                  onClick={() => setObject(n, { moves_to: on ? current.filter((x) => x !== h.id) : [...current, h.id] })}>{h.name}</button>
                              );
                            })}
                          </div>
                        </section>
                      );
                    })()}
                    {(drawn || (!person && !mo.says)) && (
                      <section className="gb-obj-sec" aria-label={`How ${o.name} is drawn`}>
                        <h5>Picture and label</h5>
                        {drawn && (
                          <Labelled label="How it looks" hint="What it looks like - not how it behaves." wide>
                            <input type="text" maxLength={220} aria-label={`How ${o.name} looks`} placeholder="e.g. a clear glass jug with a handle"
                              value={o.looks || ''} onChange={(e) => setObject(n, { looks: e.target.value || null })} />
                          </Labelled>
                        )}
                        {!person && !mo.says && (
                          <Labelled label="Words printed on it (optional)" wide>
                            <input type="text" className="gb-path-says" maxLength={40} aria-label={`Words on ${o.name}`}
                              placeholder="shown as a tag - e.g. Water"
                              value={o.label || ''} onChange={(e) => setObject(n, { label: e.target.value || undefined })} />
                          </Labelled>
                        )}
                      </section>
                    )}
                    {mo.says && (
                      <section className="gb-obj-sec">
                        <h5>What they say</h5>
                        <input type="text" className="gb-path-says" maxLength={140} aria-label={`What ${o.name} says`} placeholder="at the start (optional)"
                          value={says?.text || ''} onChange={(e) => setStart(o.id, 'says', e.target.value ? { object: o.id, event: 'talks', text: e.target.value } : null)} />
                      </section>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <button type="button" className="gb-link" onClick={addObject}>+ Add an object</button>
      </section>

      <section className="gb-panel" data-tour="pe-end">
        <h4>How it must end</h4>
        <ul className="gb-path-list">
          {path.end.map((x, i) => (Array.isArray(x)
            ? (
              <li key={`grp-${i}`} className="gb-path-row gb-path-order" aria-label="In this order">
                <strong className="gb-small">In this order:</strong>
                <ol className="gb-path-steps">
                  {x.map((y, j) => {
                    const set = (next) => setGroup(i, x.map((z, k) => (k === j ? next : z)));
                    return (
                      <li key={`grp-${i}-${j}`}>
                        <div className="gb-path-line">
                          {endFields(y, set, () => setGroup(i, x.filter((_, k) => k !== j)), `Take ${lineLabel(y)} out of the order`,
                            j > 0 && (
                              <button type="button" className="gb-link" aria-label={`Do ${lineLabel(y)} earlier`}
                                onClick={() => setGroup(i, x.map((z, k) => (k === j - 1 ? x[j] : k === j ? x[j - 1] : z)))}>↑ earlier</button>
                            ))}
                        </div>
                        {toolChips(y, set)}
                      </li>
                    );
                  })}
                </ol>
                <div className="gb-path-order-foot">
                  <button type="button" className="gb-link" onClick={() => undoOrder(i)}>Any order</button>
                  <button type="button" className="gb-link" aria-label="Delete these lines" onClick={() => removeEnd(i)}>Delete</button>
                </div>
              </li>
            )
            : endRow(x, i)))}
        </ul>
        <button type="button" className="gb-link" onClick={addEnd}>+ Add a line</button>
        {picked.length > 1 && (
          <button type="button" className="gb-btn gb-btn-sm" onClick={makeOrder}>Make the ticked lines an order (top to bottom)</button>
        )}
      </section>
      </>
      )}

      {(() => {                                                                                  // never folded away
        const warned = said ? said.filter((n) => n.kind !== 'adjusted').map((n) => ({ key: n.text, text: n.text, missing: n.kind === 'missing' }))
          : notes.filter((n) => n.startsWith('⚠')).map(ruleNote);
        const missing = warned.filter((w) => w.missing);
        const other = warned.filter((w) => !w.missing);
        return warned.length > 0 && (
          <section className="gb-panel gb-path-gaps" aria-label="Not in this simulation">
            {missing.length > 0 && (
              <>
                <p className="gb-small"><strong>Not practised in this simulation</strong> - fine if another mission covers it, or add it under <em>Edit the design</em>:</p>
                <ul className="gb-small">{missing.map((w) => <li key={w.key}>{w.text}</li>)}</ul>
              </>
            )}
            {other.length > 0 && (
              <>
                <p className="gb-small"><strong>Worth a look</strong></p>
                <ul className="gb-small">{other.map((w) => <li key={w.key}>{w.text}</li>)}</ul>
              </>
            )}
          </section>
        );
      })()}
      {(() => {
        const adjusted = said ? said.filter((n) => n.kind === 'adjusted').map((n) => n.text) : notes.filter((n) => !n.startsWith('⚠'));
        return editing && adjusted.length > 0 && (
          <details className="gb-panel">
            <summary className="gb-small">Adjusted for you ({adjusted.length})</summary>
            <ul className="gb-small">{adjusted.map((n) => <li key={n}>{n}</li>)}</ul>
          </details>
        );
      })()}

      {error && (
        <div className="gb-error" role="alert">
          <span>{error}</span>
          {refused && <button type="button" className="gb-btn gb-btn-sm gb-btn-quiet" onClick={() => window.location.reload()}>Reload</button>}
        </div>
      )}
      <div className="gb-actions">
        {(!view.approved || changed) && (
          <button type="button" className="gb-btn" data-tour="pe-approve" disabled={busy || saving || refused || path.objects.length === 0}
            onClick={() => approve()}>
            {view.approved ? 'Apply changes' : 'Build this simulation'}
          </button>
        )}
        {plain && (
          <button type="button" className="gb-btn gb-btn-quiet" disabled={saving} data-tour="pe-details"
            onClick={async () => { if (editing) await flush(); setEditing(!editing); }}>
            {editing ? 'Back to the summary' : 'Edit the design'}
          </button>
        )}
        <span className="gb-small gb-muted" role="status">
          {saving ? 'Saving…' : !view.approved ? 'Writes what people say and paints the scene - about a minute'
            : !changed ? '' : rewrite.length ? `Writes the words for ${rewrite.join(', ')} - everything else stays as it is`
              : 'No words to write - it rebuilds in seconds'}
          {view.approved && changed && !saving && (
            <span className="gb-path-rewrite">Any new pictures are drawn in one go.{' '}
              <button type="button" className="gb-link" disabled={busy || refused} onClick={() => {
                // eslint-disable-next-line no-alert
                if (window.confirm('Write every object\'s words and the opening lines again? (one AI call) The pictures stay.')) approve(true);
              }}>Rewrite everything instead</button>
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
